<template>
  <section class="page-wrap">
    <UiPageHero
      eyebrow="Dashboard"
      title="Tổng quan tài sản"
      description="Tổng quan thiết bị và thống kê 12 tháng."
    >
      <template #actions>
        <UiButton
          variant="white"
          icon="refresh"
          :disabled="loading"
          @click="loadData"
        >
          {{ loading ? "Đang tải..." : "Làm mới" }}
        </UiButton>
      </template>
    </UiPageHero>

    <div class="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
      <article v-for="card in statCards" :key="card.title" class="panel-pad">
        <div class="flex items-center justify-between gap-3">
          <div class="text-left">
            <p class="text-sm font-semibold text-slate-500">
              {{ card.title }}
            </p>
            <p class="mt-1 text-2xl font-black text-ink">{{ card.value }}</p>
          </div>
          <span
            class="grid h-9 w-9 place-items-center rounded-xl"
            :class="card.badge"
          >
            {{ card.short }}
          </span>
        </div>
        <p class="mt-2 text-left text-[11px] text-slate-500">
          {{ card.caption }}
        </p>
      </article>
    </div>

    <div
      class="mt-3 grid gap-3 xl:grid-cols-[minmax(0,1.55fr)_minmax(320px,0.85fr)]"
    >
      <section class="panel-pad min-w-0">
        <ChartBox
          title="Xu hướng tài sản trong 12 tháng"
          subtitle="So sánh tiếp nhận, hết hạn và biến động"
          class="h-[500px]"
        >
          <VChart autoresize :option="trendLineOption" />
        </ChartBox>
      </section>

      <div class="grid gap-3">
        <section class="panel-pad min-w-0">
          <ChartBox
            title="Tỷ lệ tiếp nhận so với tổng thiết bị"
            class="h-[240px]"
          >
            <VChart autoresize :option="statusGaugeOption" />
          </ChartBox>
        </section>

        <section class="panel-pad min-w-0">
          <ChartBox
            title="Thiết bị được bàn giao theo khoa/phòng"
            subtitle="Đơn vị có phát sinh tiếp nhận"
            class="h-[247px]"
          >
            <VChart autoresize :option="departmentDonutOption" />
          </ChartBox>
        </section>
      </div>
    </div>

    <div
      class="mt-3 grid gap-3 xl:grid-cols-[minmax(0,1.55fr)_minmax(320px,0.85fr)]"
    >
      <section class="panel-pad min-w-0">
        <ChartBox
          title="Tiếp nhận & hết hạn"
          subtitle="Theo từng tháng"
          class="h-[300px]"
        >
          <VChart autoresize :option="receivedExpiredBarOption" />
        </ChartBox>
      </section>

      <section class="panel-pad min-w-0">
        <ChartBox
          title="Thiết bị theo khoa/phòng"
          subtitle="Theo khoa/phòng"
          class="h-[300px]"
        >
          <VChart autoresize :option="currentDevicePieOption" />
        </ChartBox>
      </section>
    </div>

    <p
      v-if="error"
      class="mt-3 rounded-xl bg-rose-50 p-3 text-xs font-semibold text-rose-700"
    >
      {{ error }}
    </p>
  </section>
</template>

<script lang="ts">
import { computed, defineComponent, h, onMounted, ref } from "vue";
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { BarChart, LineChart, PieChart, GaugeChart } from "echarts/charts";
import {
  GraphicComponent,
  GridComponent,
  LegendComponent,
  TooltipComponent,
  TitleComponent,
} from "echarts/components";
import VChart from "vue-echarts";
import QLTSRepository from "@/services/QLTSRepository";

use([
  CanvasRenderer,
  BarChart,
  LineChart,
  PieChart,
  GaugeChart,
  GraphicComponent,
  GridComponent,
  LegendComponent,
  TooltipComponent,
  TitleComponent,
]);

const palette = [
  "#6366f1",
  "#22c55e",
  "#06b6d4",
  "#f59e0b",
  "#ef4444",
  "#94a3b8",
];

const chartFontFamily = "'Be Vietnam Pro', Arial, sans-serif";

const ChartBox = defineComponent({
  props: {
    title: { type: String, required: true },
    subtitle: { type: String, default: "" },
    class: { type: String, default: "" },
  },
  setup(props: any, { slots }: any) {
    return () =>
      h("div", { class: ["min-w-0", props.class] }, [
        h(
          "div",
          { class: "mb-2 flex items-start justify-between gap-3 text-left" },
          [
            h("div", null, [
              h("p", { class: "text-base font-bold text-ink" }, props.title),
            ]),
          ],
        ),
        h(
          "div",
          { class: "h-[calc(100%-34px)] min-h-0" },
          slots.default ? slots.default() : null,
        ),
      ]);
  },
});

const asArray = (value: any): any[] =>
  Array.isArray(value) ? value : value ? [value] : [];

const getNumber = (item: any): number =>
  Number(
    (item &&
      (item.value ||
        item.soLuong ||
        item.soluong ||
        item.count ||
        item.tong ||
        item.total ||
        item.soLuongTiepNhan ||
        item.soLuongQuaHan)) ||
      0,
  );

const getLiquidationNumber = (item: any): number =>
  Number((item && item.soLuongThanhLy) || 0);

const getLabel = (item: any, index: number): string =>
  String(
    (item &&
      (item.label ||
        item.name ||
        item.ten ||
        item.tenKhoaPhong ||
        item.khoaPhong ||
        item.thang ||
        item.month)) ||
      `M${index + 1}`,
  ).trim();

const monthLabels = Array.from({ length: 12 }, (_, index) => `M${index + 1}`);

const baseTooltip = {
  trigger: "axis",
  backgroundColor: "rgba(15,23,42,0.92)",
  borderWidth: 0,
  textStyle: { color: "#fff", fontSize: 11, fontFamily: chartFontFamily },
};

export default {
  components: { VChart, ChartBox },
  setup() {
    const loading = ref(false);
    const error = ref("");
    const hienCo = ref<any[]>([]);
    const tiepNhan = ref<any[]>([]);
    const quaHan = ref<any[]>([]);
    const thongKe = ref<any[]>([]);
    const theoKhoa = ref<any[]>([]);

    const loadData = () => {
      loading.value = true;
      error.value = "";
      Promise.all([
        QLTSRepository.getListThongKeThietBiHienCo(),
        QLTSRepository.getListSoLuongTiepNhanTrong12Thang(),
        QLTSRepository.getListSoLuongQuaHanTrong12Thang(),
        QLTSRepository.getListThongKeTrong12Thang(),
        QLTSRepository.getListThongKeTheoKhoaTrong12Thang(),
      ])
        .then(([a, b, c, d, e]) => {
          console.log(e.data);
          hienCo.value = asArray(a.data);
          tiepNhan.value = asArray(b.data);
          quaHan.value = asArray(c.data);
          thongKe.value = asArray(d.data);
          theoKhoa.value = asArray(e.data);
          loading.value = false;
        })
        .catch(() => {
          error.value = "Không tải được dữ liệu thống kê.";
          loading.value = false;
        });
    };

    const totalCurrent = computed(() =>
      hienCo.value.reduce((sum, item) => sum + getNumber(item), 0),
    );
    const totalReceived = computed(() =>
      tiepNhan.value.reduce((sum, item) => sum + getNumber(item), 0),
    );
    const totalExpired = computed(() =>
      quaHan.value.reduce((sum, item) => sum + getNumber(item), 0),
    );
    const totalMovement = computed(() =>
      thongKe.value.reduce((sum, item) => sum + getLiquidationNumber(item), 0),
    );

    const statCards = computed(() => [
      {
        title: "Tổng thiết bị",
        value: totalCurrent.value,
        caption: "Tài sản đang ghi nhận",
        short: "TB",
        badge: "bg-indigo-50 text-indigo-700",
      },
      {
        title: "Tiếp nhận",
        value: totalReceived.value,
        caption: "Phát sinh trong 12 tháng",
        short: "TN",
        badge: "bg-emerald-50 text-emerald-700",
      },
      {
        title: "Hết hạn",
        value: totalExpired.value,
        caption: "Cần kiểm tra xử lý",
        short: "HH",
        badge: "bg-amber-50 text-amber-700",
      },
      {
        title: "Thanh lý",
        value: totalMovement.value,
        caption: "Tài sản đã thanh lý",
        short: "TL",
        badge: "bg-rose-50 text-rose-700",
      },
    ]);

    const departmentData = computed(() => {
      const source = theoKhoa.value.length ? theoKhoa.value : hienCo.value;
      return source.map((item, index) => ({
        name: getLabel(item, index),
        value: getNumber(item),
      }));
    });

    console.log("Department Data:", departmentData.value);

    const currentDeviceData = computed(() =>
      hienCo.value.map((item, index) => ({
        name: getLabel(item, index),
        value: getNumber(item),
      })),
    );

    const receivedSeries = computed(() =>
      monthLabels.map((_, index) => getNumber(tiepNhan.value[index])),
    );
    const expiredSeries = computed(() =>
      monthLabels.map((_, index) => getNumber(quaHan.value[index])),
    );
    const movementSeries = computed(() =>
      monthLabels.map((_, index) => getLiquidationNumber(thongKe.value[index])),
    );

    const receivedExpiredBarOption = computed(() => ({
      color: ["#6366f1", "#f59e0b"],
      textStyle: { fontFamily: chartFontFamily },
      tooltip: baseTooltip,
      legend: {
        bottom: 0,
        itemWidth: 12,
        itemHeight: 8,
        textStyle: { fontSize: 10, fontFamily: chartFontFamily },
      },
      grid: { top: 10, right: 10, bottom: 40, left: 28 },
      xAxis: {
        type: "category",
        data: monthLabels,
        axisLabel: { fontSize: 10, fontFamily: chartFontFamily },
      },
      yAxis: {
        type: "value",
        splitLine: { lineStyle: { type: "dashed", color: "#e5e7eb" } },
        axisLabel: { fontSize: 10, fontFamily: chartFontFamily },
      },
      series: [
        {
          name: "Tiếp nhận",
          type: "bar",
          data: receivedSeries.value,
          barWidth: 8,
          itemStyle: { borderRadius: [6, 6, 0, 0] },
        },
        {
          name: "Hết hạn",
          type: "bar",
          data: expiredSeries.value,
          barWidth: 8,
          itemStyle: { borderRadius: [6, 6, 0, 0] },
        },
      ],
    }));

    const trendLineOption = computed(() => ({
      color: ["#6366f1", "#22c55e", "#94a3b8"],
      textStyle: { fontFamily: chartFontFamily },
      tooltip: baseTooltip,
      legend: {
        bottom: 0,
        textStyle: {
          fontSize: 11,
          color: "#475569",
          fontFamily: chartFontFamily,
        },
      },
      grid: { top: 28, right: 20, bottom: 42, left: 36 },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: monthLabels,
        axisLabel: { fontSize: 10, fontFamily: chartFontFamily },
      },
      yAxis: {
        type: "value",
        splitLine: { lineStyle: { type: "dashed", color: "#e5e7eb" } },
        axisLabel: { fontSize: 10, fontFamily: chartFontFamily },
      },
      series: [
        {
          name: "Tiếp nhận",
          type: "line",
          smooth: true,
          data: receivedSeries.value,
          areaStyle: { opacity: 0.13 },
          symbolSize: 5,
        },
        {
          name: "Hết hạn",
          type: "line",
          smooth: true,
          data: expiredSeries.value,
          areaStyle: { opacity: 0.12 },
          symbolSize: 5,
        },
        {
          name: "Thanh lý",
          type: "line",
          smooth: true,
          data: movementSeries.value,
          areaStyle: { opacity: 0.1 },
          symbolSize: 5,
        },
      ],
    }));

    const departmentDonutOption = computed(() => ({
      color: palette,
      textStyle: { fontFamily: chartFontFamily },
      tooltip: { trigger: "item" },
      legend: {
        orient: "vertical",
        right: 12,
        top: "middle",
        itemWidth: 18,
        itemHeight: 10,
        itemGap: 10,
        textStyle: {
          fontSize: 11,
          color: "#475569",
          fontFamily: chartFontFamily,
          width: 150,
          overflow: "truncate",
        },
      },
      series: [
        {
          type: "pie",
          radius: ["46%", "70%"],
          center: ["38%", "50%"],
          data: departmentData.value,
          label: {
            formatter: "{d}%",
            fontSize: 10,
            fontFamily: chartFontFamily,
          },
          labelLine: { length: 8, length2: 6 },
        },
      ],
      graphic: {
        type: "text",
        left: "36%",
        top: "43%",
        style: {
          textAlign: "center",
          fill: "#1e3a8a",
          fontSize: 11,
          fontFamily: chartFontFamily,
          fontWeight: 700,
        },
      },
      media: [
        {
          query: { maxWidth: 460 },
          option: {
            legend: {
              orient: "horizontal",
              left: "center",
              right: "auto",
              top: "auto",
              bottom: 0,
              itemGap: 12,
              textStyle: {
                fontSize: 10,
                color: "#475569",
                fontFamily: chartFontFamily,
                width: 92,
                overflow: "truncate",
              },
            },
            series: [
              {
                radius: ["40%", "62%"],
                center: ["50%", "43%"],
                label: {
                  formatter: "{d}%",
                  fontSize: 10,
                  fontFamily: chartFontFamily,
                },
                labelLine: { length: 6, length2: 4 },
              },
            ],
            graphic: {
              left: "center",
              top: "37%",
            },
          },
        },
      ],
    }));

    const currentDevicePieOption = computed(() => ({
      color: palette,
      textStyle: { fontFamily: chartFontFamily },
      tooltip: { trigger: "item" },
      legend: {
        orient: "vertical",
        right: 8,
        top: "middle",
        itemWidth: 18,
        itemHeight: 10,
        itemGap: 10,
        textStyle: {
          fontSize: 11,
          color: "#475569",
          fontFamily: chartFontFamily,
          width: 150,
          overflow: "truncate",
        },
      },
      series: [
        {
          type: "pie",
          radius: "64%",
          center: ["34%", "50%"],
          data: currentDeviceData.value,
          label: {
            formatter: "{d}%",
            fontSize: 10,
            fontFamily: chartFontFamily,
          },
          labelLine: { length: 8, length2: 6 },
          itemStyle: {
            borderColor: "#fff",
            borderWidth: 2,
          },
        },
      ],
      media: [
        {
          query: { maxWidth: 460 },
          option: {
            legend: {
              orient: "horizontal",
              left: "center",
              right: "auto",
              top: "auto",
              bottom: 0,
              itemGap: 12,
              textStyle: {
                fontSize: 10,
                color: "#475569",
                fontFamily: chartFontFamily,
                width: 92,
                overflow: "truncate",
              },
            },
            series: [
              {
                radius: "58%",
                center: ["50%", "43%"],
                label: {
                  formatter: "{d}%",
                  fontSize: 10,
                  fontFamily: chartFontFamily,
                },
                labelLine: { length: 6, length2: 4 },
              },
            ],
          },
        },
      ],
    }));

    const statusGaugeOption = computed(() => ({
      color: palette,
      textStyle: { fontFamily: chartFontFamily },
      tooltip: { trigger: "item" },
      series: [
        {
          type: "gauge",
          center: ["50%", "46%"],
          radius: "72%",
          startAngle: 90,
          endAngle: -270,
          pointer: { show: false },
          progress: {
            show: true,
            overlap: false,
            roundCap: true,
            clip: false,
            itemStyle: { color: "#0d63c7" },
          },
          axisLine: { lineStyle: { width: 10, color: [[1, "#e2e8f0"]] } },
          splitLine: { show: false },
          axisTick: { show: false },
          axisLabel: { show: false },
          data: [
            {
              value: totalCurrent.value
                ? Math.round((totalReceived.value / totalCurrent.value) * 100)
                : 0,
              name: "Tỷ lệ tiếp nhận",
              title: {
                offsetCenter: ["0%", "32%"],
                fontSize: 12,
                color: "#475569",
                fontFamily: chartFontFamily,
              },
              detail: {
                offsetCenter: ["0%", "-6%"],
                formatter: "{value}%",
                fontSize: 18,
                fontWeight: 800,
                color: "#10233f",
                fontFamily: chartFontFamily,
              },
            },
          ],
        },
      ],
    }));

    onMounted(loadData);
    return {
      loading,
      error,
      loadData,
      statCards,
      receivedExpiredBarOption,
      trendLineOption,
      departmentDonutOption,
      currentDevicePieOption,
      statusGaugeOption,
    };
  },
};
</script>
